import {
    Component,
    OnInit,
    Input,
    ViewChild,
    inject,
    ChangeDetectionStrategy,
    DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScreenService } from '../../shared/services';
import { DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { DxDrawerTypes, DxDrawerComponent } from 'devextreme-angular/ui/drawer';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';

import { Router, NavigationEnd } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { SideNavigationMenuComponent } from '../../shared/components/side-navigation-menu/side-navigation-menu.component';

@Component({
    selector: 'app-side-nav-outer-toolbar',
    templateUrl: './side-nav-outer-toolbar.component.html',
    styleUrls: ['./side-nav-outer-toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        DxDrawerComponent,
        DxTemplateDirective,
        SideNavigationMenuComponent,
        DxScrollViewComponent,
    ],
})
export class SideNavOuterToolbarComponent implements OnInit {
    private screen = inject(ScreenService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);

    @ViewChild(DxScrollViewComponent, { static: true })
    scrollView!: DxScrollViewComponent;
    selectedRoute = '';

    menuOpened!: boolean;
    temporaryMenuOpened = false;

    @Input()
    title!: string;

    menuMode: DxDrawerTypes.OpenedStateMode = 'shrink';
    menuRevealMode: DxDrawerTypes.RevealMode = 'expand';
    minMenuSize = 0;
    shaderEnabled = false;

    ngOnInit() {
        this.menuOpened = this.screen.sizes['screen-large'];

        // The initial NavigationEnd can fire before this component exists,
        // so seed the selection from the current URL.
        this.selectedRoute = this.stripUrl(this.router.url);

        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((val) => {
                if (val instanceof NavigationEnd) {
                    this.selectedRoute = this.stripUrl(val.urlAfterRedirects);
                }
            });

        this.screen.changed
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.updateDrawer());

        this.updateDrawer();
    }

    // Menu item keys are plain paths — drop query params and fragments.
    private stripUrl(url: string) {
        return url.split(/[?#]/)[0];
    }

    updateDrawer() {
        const isXSmall = this.screen.sizes['screen-x-small'];
        const isLarge = this.screen.sizes['screen-large'];

        this.menuMode = isLarge ? 'shrink' : 'overlap';
        this.menuRevealMode = isXSmall ? 'slide' : 'expand';
        this.minMenuSize = isXSmall ? 0 : 60;
        this.shaderEnabled = !isLarge;
    }

    get hideMenuAfterNavigation() {
        return this.menuMode === 'overlap' || this.temporaryMenuOpened;
    }

    get showMenuAfterClick() {
        return !this.menuOpened;
    }

    navigationChanged(event: DxTreeViewTypes.ItemClickEvent) {
        const path = (event.itemData as any).path;
        const pointerEvent = event.event;

        if (path && this.menuOpened) {
            if (event.node?.selected) {
                pointerEvent?.preventDefault();
            } else {
                this.router.navigate([path]);
                this.scrollView.instance.scrollTo(0);
            }

            if (this.hideMenuAfterNavigation) {
                this.temporaryMenuOpened = false;
                this.menuOpened = false;
                pointerEvent?.stopPropagation();
            }
        } else {
            pointerEvent?.preventDefault();
        }
    }

    navigationClick() {
        if (this.showMenuAfterClick) {
            this.temporaryMenuOpened = true;
            this.menuOpened = true;
        }
    }

    toggleMenu() {
        this.menuOpened = !this.menuOpened;
        if (!this.menuOpened) {
            this.temporaryMenuOpened = false;
        }
    }

    menuOpenedChanged(opened: boolean) {
        this.menuOpened = opened;
        // Closing without navigating (outside click, hamburger) ends a
        // temporary open; a stale flag would otherwise close the menu right
        // after the next navigation.
        if (!opened) {
            this.temporaryMenuOpened = false;
        }
    }
}
